/**
 * Workspace — the welcome home for department-bound users.
 *
 * What this page is, in one sentence: the calm, focused landing for users
 * who don't have access to PSI. Three sections, top to bottom:
 *
 *   1. Greeting — time-of-day aware, names the user, names their team.
 *   2. Today — a horizontal strip of items due in the next 7 days across
 *              every app the user has access to. Empty state is a quiet
 *              "you're all caught up." No motivational fluff.
 *   3. Apps — the SAME iPhone-style Launchpad modal PSI uses (top-right
 *             button or Ctrl/Cmd+K), opened automatically on arrival.
 *             Operator decision 2026-07-20: one launcher for everyone —
 *             the old in-page card grid drifted (showed retired apps,
 *             missed new ones) and is gone.
 *
 * Compliance users can also reach this page via deep link but it isn't
 * surfaced anywhere in their UI; their home is PSI.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AppLauncher } from '../Oracle/OracleHome';
import AgentStrip from './AgentStrip.jsx';
import { AdvicePanel } from '../Calendar/SmacRegister';
import ConditionAnswer, { fetchConditionAnswers } from '../PPM/ConditionAnswer';
import PolicyParagraphEditor, { editsSummary, toRequestEdits } from '../PPM/PolicyParagraphEditor';
import { anchorFindings, anchorReturnedEdits } from '../PPM/reviewAnchors';
import ReviewCorrespondence from '../PPM/ReviewCorrespondence';
import { TakeOnBlock, RuleHomesList } from '../PPM/RuleTakeOn';

const API = process.env.REACT_APP_API_URL;

// Match the icon palette used in AppSidebar so the welcome page and the
// rail/launcher feel like one product. Keys map to the in-Core app slug.
const APP_ICON = {
  cap:       '🎯',
  memo:      '📄',
  cil:       '📝',
  coa:       '📋',
  risk:      '⚠️',
  calendar:  '📅',
  knowledge: '📘',
  scrubber:  '🧹',
  analytics: '📊',
};

// One state-contract item, in plain words: what's needed, when it's due,
// where it comes from, and one button. No jargon, no navigation.
/**
 * Annual policy review — the owner's (or delegate's) home-page experience.
 * Confirm policies as current (one click, or select several and confirm all),
 * upload a revised Word document, or hand a policy to a teammate. Every
 * completion is sealed with a typed attestation; compliance reviews before
 * anything publishes.
 */
export function PolicyReviewSection() {
  const [tasks, setTasks] = useState([]);
  const [teammates, setTeammates] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [attestName, setAttestName] = useState('');
  const [reviewedOn, setReviewedOn] = useState(localToday());
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');

  const reload = useCallback(() => {
    fetch(`${API}/api/ppm-review/my`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d?.success) setTasks(d.tasks || []); })
      .catch(() => {});
  }, []);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    fetch(`${API}/api/ppm-review/my/teammates`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d?.success) setTeammates(d.teammates || []); })
      .catch(() => {});
  }, []);
  // Final rule requirements routed to this person's departments that no
  // policy covers yet: shown once here, with the recommended home.
  const [ruleHomes, setRuleHomes] = useState([]);
  useEffect(() => {
    fetch(`${API}/api/ppm-review/rule-homes/mine`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d?.success) setRuleHomes(d.homes || []); })
      .catch(() => {});
  }, []);

  // Arrived from the email button (/app?app=workspace&review=<campaign>):
  // the campaign id was stored before login; consume it once, scroll here,
  // and light the section up. Any other arrival lands on the page as usual.
  const [landedCampaign, setLandedCampaign] = useState(() => {
    try {
      const v = sessionStorage.getItem('pcg_open_review');
      if (v) sessionStorage.removeItem('pcg_open_review');
      return v || null;
    } catch { return null; }
  });
  const sectionRef = React.useRef(null);
  useEffect(() => {
    if (!landedCampaign || !tasks.length) return;
    const el = sectionRef.current;
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const t = setTimeout(() => setLandedCampaign(null), 6000);
    return () => clearTimeout(t);
  }, [landedCampaign, tasks.length]);

  const [mailOpen, setMailOpen] = useState(false);

  if (!tasks.length && !ruleHomes.length) return null;

  const due = tasks[0]?.due_date ? new Date(String(tasks[0].due_date).slice(0, 10) + 'T12:00:00Z')
    .toLocaleDateString('en-US', { month: 'long', day: 'numeric' }) : null;

  // Policies grouped by department, every department the user holds.
  const groups = (() => {
    const m = new Map();
    for (const t of tasks) {
      const key = t.department_name || '';
      if (!m.has(key)) m.set(key, { name: t.department_name || null, tasks: [] });
      m.get(key).tasks.push(t);
    }
    return [...m.values()].sort((a, b) => (a.name ? 0 : 1) - (b.name ? 0 : 1) || String(a.name || '').localeCompare(String(b.name || '')));
  })();

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // A policy is confirmed only after the agent has read it. Confirming a
  // policy the agent has not read starts the read first and confirms it
  // once the reading is done, so nothing goes to compliance unread.
  const postConfirm = (ids) => fetch(`${API}/api/ppm-review/tasks/confirm`, {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ task_ids: ids, attestation_name: attestName.trim(), reviewed_on: reviewedOn }),
  }).then(r => r.json());
  const waitForReads = async (ids) => {
    await Promise.all(ids.map((id) => fetch(`${API}/api/ppm-review/tasks/${id}/guidance/generate`, { method: 'POST', credentials: 'include' }).catch(() => null)));
    const pending = new Set(ids);
    const startedAt = Date.now();
    while (pending.size && Date.now() - startedAt < 30 * 60 * 1000) {
      await new Promise((r) => setTimeout(r, 6000));
      for (const id of [...pending]) {
        try {
          const g = await fetch(`${API}/api/ppm-review/tasks/${id}/guidance`, { credentials: 'include' }).then(r => r.json());
          if (g.status === 'ready' || g.status === 'failed') pending.delete(id);
        } catch { /* keep waiting */ }
      }
      setNotice(`The agent is reading ${pending.size} ${pending.size === 1 ? 'policy' : 'policies'} before they are confirmed. This can take a few minutes; you can keep working.`);
    }
    return [...pending];
  };
  const confirmSelected = async () => {
    if (!reviewedOn) { setNotice('Select the date of your review.'); return; }
    if (!attestName.trim()) { setNotice('Type your full name to attest.'); return; }
    setBusy(true); setNotice('');
    try {
      let d = await postConfirm([...selected]);
      let confirmed = d.success ? (d.confirmed || 0) : 0;
      if (d.success && (d.unread || []).length) {
        setNotice(`The agent is reading ${d.unread.length} ${d.unread.length === 1 ? 'policy' : 'policies'} before they are confirmed.`);
        const still = await waitForReads(d.unread);
        const ready = d.unread.filter((id) => !still.includes(id));
        if (ready.length) { const d2 = await postConfirm(ready); if (d2.success) confirmed += d2.confirmed || 0; }
        if (still.length) setNotice(`${still.length} ${still.length === 1 ? 'policy is' : 'policies are'} still being read. Confirm ${still.length === 1 ? 'it' : 'them'} again in a few minutes.`);
      }
      if (d.success) {
        if (confirmed) setNotice((n) => `${confirmed} ${confirmed === 1 ? 'policy' : 'policies'} confirmed and sent to compliance.${n && /still being read/.test(n) ? ` ${n}` : ''}`);
        setSelected(new Set()); setAttestName(''); reload();
      } else setNotice(d.message || 'That did not go through. Try again.');
    } catch { setNotice('That did not go through. Try again.'); }
    setBusy(false);
  };

  return (
    <section ref={sectionRef} className={`mb-12 scroll-mt-6 rounded-3xl transition-shadow ${landedCampaign ? 'ring-2 ring-orange-300 shadow-[0_0_0_6px_rgba(245,130,32,0.12)] p-4 -m-4' : ''}`}>
      <div className="flex items-baseline justify-between mb-1">
        <h2 className="text-[13px] font-semibold uppercase tracking-[0.06em] text-slate-500">Your policies — annual review</h2>
        <span className="text-[12px] text-slate-400">{tasks.length} {tasks.length === 1 ? 'policy' : 'policies'}{due ? ` · due by ${due}` : ''}</span>
      </div>
      <p className="text-[13px] text-slate-500 mb-3">
        Review each policy you own. If it is still accurate, confirm it — one click and your typed name, about a minute per policy. If it needs changes, edit it here (change, add, or erase any paragraph) or upload your revised Word or PDF version. You can also hand one to a teammate.
      </p>
      <div className="mb-3">
        <button type="button" onClick={() => setMailOpen(v => !v)} className="text-[12px] font-medium text-slate-500 hover:text-slate-700">
          {mailOpen ? 'Hide correspondence' : 'Correspondence — the review emails and your replies'}
        </button>
        {mailOpen && <div className="mt-2"><ReviewCorrespondence scope="mine" /></div>}
      </div>

      <div className="mb-3 flex items-center gap-2 flex-wrap">
        <button type="button" onClick={() => setSelected(new Set(tasks.map(t => t.id)))}
          className="px-3 py-2 rounded-xl ring-1 ring-slate-200 text-[13px] text-slate-600 hover:ring-slate-300">
          Select all
        </button>
        <span className="text-[12px] text-slate-400">Tick the policies that are still current — the confirmation bar follows you.</span>
        {notice && <span className="text-[13px] font-medium text-emerald-700">{notice}</span>}
      </div>

      {ruleHomes.length > 0 && (
        <div className="mb-5 rounded-xl ring-1 ring-orange-200 bg-orange-50/40 p-4">
          <p className="text-[13px] font-semibold text-slate-800">New in the final rule for your department, not in any policy yet</p>
          <p className="text-[12px] text-slate-500 mt-0.5 mb-1">Each item is a requirement the organization must meet that no policy states. The recommended policy is named where the reader was sure; where it was not, it says so and compliance decides.</p>
          <RuleHomesList homes={ruleHomes} compact />
        </div>
      )}

      <div className="space-y-5">
        {groups.map(g => (
          <div key={g.name || '__none'}>
            {(groups.length > 1 || g.name) && (
              <p className="text-[12px] font-semibold uppercase tracking-[0.05em] text-slate-400 mb-2">{g.name || 'No department'} · {g.tasks.length}</p>
            )}
            <div className="space-y-3">
              {g.tasks.map(t => (
                <PolicyReviewCard key={t.id} task={t} teammates={teammates}
                  selected={selected.has(t.id)} onToggle={() => toggle(t.id)} onDone={reload} />
              ))}
            </div>
          </div>
        ))}
      </div>

      {selected.size > 0 && <div className="h-32" aria-hidden="true" />}
      {selected.size > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 w-[min(680px,calc(100vw-2rem))] rounded-2xl bg-white shadow-xl ring-1 ring-slate-200 p-4">
          <p className="text-[12.5px] text-slate-700 mb-2">
            I have reviewed {selected.size === 1 ? 'this policy' : `these ${selected.size} policies`} and attest that {selected.size === 1 ? 'it is' : 'they are'} accurate, current, and complete to the best of my knowledge and ability.
          </p>
          <div className="flex gap-2 flex-wrap items-center">
            <label className="flex items-center gap-1.5 text-[12px] text-slate-500">Date of review
              <input type="date" value={reviewedOn} onChange={e => setReviewedOn(e.target.value)}
                className="rounded-xl ring-1 ring-slate-200 px-2.5 py-2 text-[13px] text-slate-700 outline-none focus:ring-2 focus:ring-orange-300" />
            </label>
            <input value={attestName} onChange={e => setAttestName(e.target.value)} placeholder="Type your full name"
              className="flex-1 min-w-[180px] rounded-xl ring-1 ring-slate-200 px-3.5 py-2 text-[13px] outline-none focus:ring-2 focus:ring-orange-300" />
            <button type="button" disabled={busy} onClick={confirmSelected}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white text-[13px] font-semibold disabled:opacity-50">
              {busy ? 'Submitting…' : `Confirm selected as current (${selected.size})`}
            </button>
            <button type="button" onClick={() => setSelected(new Set())} className="px-3 py-2 text-[13px] text-slate-500">Clear</button>
          </div>
        </div>
      )}
    </section>
  );
}

function localToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function PolicyReviewCard({ task, teammates, selected, onToggle, onDone }) {
  const [mode, setMode] = useState(null); // null | 'read' | 'replace' | 'delegate'
  const [file, setFile] = useState(null);
  const [summary, setSummary] = useState('');
  const [attestName, setAttestName] = useState('');
  const [reviewedOn, setReviewedOn] = useState(localToday());
  const [proposals, setProposals] = useState({}); // paragraph index -> { after, heading }
  const [discuss, setDiscuss] = useState(null);   // { index, selected }
  const [delegateId, setDelegateId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [policyDoc, setPolicyDoc] = useState(null); // { version, files } | { missing: true }

  const label = `${task.policy_number ? `${task.policy_number}: ` : ''}${task.title}`;

  const [docParas, setDocParas] = useState(null);   // { format, paragraphs } | null
  const [paraOps, setParaOps] = useState([]);       // [{ index, op, text }] — change / add / erase
  const [guideData, setGuideData] = useState(null);
  const [drafted, setDrafted] = useState(() => new Set());
  const [placing, setPlacing] = useState(false);

  // Verified findings become suggested WORDING placed on the policy itself
  // (operator order 2026-09-02): only flags that survived word-for-word
  // verification draft anything; conditional findings wait for the org's
  // answer instead of guessing. Unverified observations place nothing.
  useEffect(() => {
    if (!guideData || docParas?.format !== 'docx' || task.status === 'submitted') return undefined;
    // Every CONFIRMED finding gets wording: a verified flag, and a conditional
    // one once the organization has answered yes to its condition (the answer
    // is read fresh each time this runs).
    let condAnswers = null;
    const flaggedOf = (answers) => (guideData.guidance?.sections || []).filter((sec) =>
      sec.status === 'flag' && (sec.citations || []).length && !drafted.has(sec.heading)
      && (sec.applies !== 'conditional' || (sec.condition && answers?.get(sec.condition)?.answer === 'yes')));
    const flagged = flaggedOf(null);
    // Requirements this policy was named to take on (new in the rule, stated
    // by no policy) are drafted onto the text the same way, grounded on the
    // regulation's own sentence, as a paragraph the owner accepts or not.
    const takeOn = (guideData.guidance?.take_on || []).filter((r) => r.quote && !drafted.has(`take_on:${r.id}`)).map((r) => ({
      key: `take_on:${r.id}`, heading: 'Full policy',
      note: `Add to this policy the following requirement from the final rule (42 CFR ${r.cfr_section}), which no policy currently states: ${r.plain || r.title}`,
      citations: [{ quote: r.quote }],
    }));
    let alive = true;
    (async () => {
      condAnswers = await fetchConditionAnswers();
      const toPlace = [...flaggedOf(condAnswers).map((sec) => ({ ...sec, key: sec.heading })), ...takeOn];
      if (!toPlace.length || !alive) return;
      setPlacing(true);
      for (const sec of toPlace) {
        if (!alive) break;
        setDrafted((prev) => new Set([...prev, sec.key]));
        try {
          const d = await fetch(`${API}/api/ppm-review/tasks/${task.id}/draft-change`, {
            method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ heading: sec.heading, note: sec.note, quotes: (sec.citations || []).map(c => c.quote).filter(Boolean).slice(0, 6) }),
          }).then(r => r.json());
          if (alive && d.success && (d.edits || []).length) {
            setProposals((prev) => {
              const next = { ...prev };
              const citation = sec.citations?.[0] || null;
              for (const e of d.edits) if (next[e.index] == null) next[e.index] = { after: e.after, heading: d.heading, citation };
              return next;
            });
          }
        } catch { /* verified or nothing — a failed draft places nothing */ }
      }
      if (alive) setPlacing(false);
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guideData, docParas, task.id, task.status]);

  const openRead = async () => {
    if (mode === 'read') { setMode(null); return; }
    // The document opens as its own page — a modal of the actual Word doc.
    setMode('read'); setError('');
    if (!policyDoc) {
      try {
        const d = await fetch(`${API}/api/ppm/library/${task.policy_id}`, { credentials: 'include' }).then(r => r.json());
        if (d.success) setPolicyDoc({ version: d.version, files: d.files || [] });
        else setPolicyDoc({ missing: true });
      } catch { setPolicyDoc({ missing: true }); }
    }
    if (!docParas) {
      try {
        const d = await fetch(`${API}/api/ppm-review/tasks/${task.id}/document`, { credentials: 'include' }).then(r => r.json());
        setDocParas(d.success ? d : { format: 'other' });
        // Compliance's edits from a return sit on the text as suggested wording.
        if (d.success && Array.isArray(d.compliance_suggestions) && d.compliance_suggestions.length) {
          const by = d.compliance_suggestions_by || 'Compliance';
          setProposals((prev) => {
            const next = { ...prev };
            for (const e of d.compliance_suggestions) if (e.op === 'replace' && next[e.index] == null) next[e.index] = { after: e.after, heading: `${by} suggests`, citation: null };
            return next;
          });
        }
      } catch { setDocParas({ format: 'other' }); }
      // The review saved earlier comes back where it was left.
      try {
        const s = await fetch(`${API}/api/ppm-review/tasks/${task.id}/draft`, { credentials: 'include' }).then(r => r.json());
        if (s.success && s.draft) {
          if (Array.isArray(s.draft.edits) && s.draft.edits.length) setParaOps((prev) => (prev.length ? prev : s.draft.edits));
          if (s.draft.change_summary) setSummary((prev) => prev || s.draft.change_summary);
          setDraftSavedAt(s.saved_at || null);
        }
      } catch { /* no saved review */ }
    }
  };

  const editCount = toRequestEdits(paraOps).length;
  const editSummary = editsSummary(toRequestEdits(paraOps));
  const submitEdits = async () => {
    if (!reviewedOn) { setError('Select the date of your review.'); return; }
    if (!attestName.trim()) { setError('Type your full name to attest.'); return; }
    setBusy(true); setError('');
    try {
      const d = await fetch(`${API}/api/ppm-review/tasks/${task.id}/edit`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          edits: toRequestEdits(paraOps),
          change_summary: summary || null,
          attestation_name: attestName.trim(),
          reviewed_on: reviewedOn,
        }),
      }).then(r => r.json());
      if (d.success) {
        // Submitted: the saved draft has done its job.
        fetch(`${API}/api/ppm-review/tasks/${task.id}/draft`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ edits: [], change_summary: '' }) }).catch(() => {});
        setFinish(false); setMode(null); onDone();
      }
      else setError(d.message || 'That did not go through. Try again.');
    } catch { setError('That did not go through. Try again.'); }
    setBusy(false);
  };

  const submitReplace = async () => {
    if (!file) { setError('Attach the revised policy (Word .docx or PDF).'); return; }
    if (!reviewedOn) { setError('Select the date of your review.'); return; }
    if (!attestName.trim()) { setError('Type your full name to attest.'); return; }
    setBusy(true); setError('');
    try {
      const fd = new FormData();
      fd.append('document', file);
      if (summary) fd.append('change_summary', summary);
      fd.append('attestation_name', attestName.trim());
      fd.append('reviewed_on', reviewedOn);
      const d = await fetch(`${API}/api/ppm-review/tasks/${task.id}/replace`, {
        method: 'POST', credentials: 'include', body: fd,
      }).then(r => r.json());
      if (d.success) { setMode(null); onDone(); }
      else setError(d.message || 'That did not go through. Try again.');
    } catch { setError('That did not go through. Try again.'); }
    setBusy(false);
  };

  // Save, and Save and submit for approval (operator order 2026-10-02). While
  // the owner reviews, a bar at the top of the reading window keeps both in
  // reach: Save keeps the edits and summary on the task so they can come back
  // later; Save and submit for approval asks for the name and date and sends
  // the review. With edits it submits them exactly as the box under the text
  // does; with none it confirms the policy as current exactly as the bottom
  // bar does. Same actions, same records. Closing the window saves too.
  const [finish, setFinish] = useState(false);
  const [finishNotice, setFinishNotice] = useState('');
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [draftBusy, setDraftBusy] = useState(false);
  const saveDraft = async (quiet = false) => {
    if (task.status === 'submitted') return true;
    if (!quiet) setDraftBusy(true);
    try {
      const d = await fetch(`${API}/api/ppm-review/tasks/${task.id}/draft`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ edits: paraOps, change_summary: summary || '' }),
      }).then(r => r.json());
      if (d.success) { setDraftSavedAt(d.saved_at || new Date().toISOString()); if (!quiet) setError(''); return true; }
      if (!quiet) setError(d.message || 'The review could not be saved. Try again.');
    } catch { if (!quiet) setError('The review could not be saved. Try again.'); }
    finally { if (!quiet) setDraftBusy(false); }
    return false;
  };
  const closeWindow = async () => { await saveDraft(true); setMode(null); };
  const openSubmit = () => { setError(''); setFinishNotice(''); setFinish(true); };
  const finishReview = async () => {
    if (editCount > 0) { await submitEdits(); return; }
    if (!reviewedOn) { setError('Select the date of your review.'); return; }
    if (!attestName.trim()) { setError('Type your full name to attest.'); return; }
    setBusy(true); setError(''); setFinishNotice('');
    try {
      const d = await fetch(`${API}/api/ppm-review/tasks/confirm`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ task_ids: [task.id], attestation_name: attestName.trim(), reviewed_on: reviewedOn }),
      }).then(r => r.json());
      if (d.success && (d.unread || []).includes(task.id)) {
        fetch(`${API}/api/ppm-review/tasks/${task.id}/guidance/generate`, { method: 'POST', credentials: 'include' }).catch(() => {});
        setFinishNotice('The agent is reading this policy first. Finish again in a few minutes and it will be confirmed.');
      } else if (d.success) { setFinish(false); onDone(); }
      else setError(d.message || 'That did not go through. Try again.');
    } catch { setError('That did not go through. Try again.'); }
    setBusy(false);
  };

  const submitDelegate = async () => {
    if (!delegateId) { setError('Pick a teammate.'); return; }
    setBusy(true); setError('');
    try {
      const d = await fetch(`${API}/api/ppm-review/tasks/${task.id}/delegate`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: delegateId }),
      }).then(r => r.json());
      if (d.success) { setMode(null); onDone(); }
      else setError(d.message || 'That did not go through. Try again.');
    } catch { setError('That did not go through. Try again.'); }
    setBusy(false);
  };

  return (
    <div className={`rounded-2xl bg-white ring-1 p-5 ${task.status === 'returned' ? 'ring-amber-300' : 'ring-slate-200'} ${selected ? 'ring-2 ring-orange-300' : ''}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <input type="checkbox" checked={selected} onChange={onToggle} aria-label={`Select ${label}`}
            className="mt-1 h-4 w-4 rounded border-slate-300 text-orange-600 focus:ring-orange-400" />
          <div className="min-w-0">
            <p className="text-[15px] font-semibold text-slate-900">{label}</p>
            {task.status === 'returned' && task.decision_note && (
              <p className="text-[13px] text-amber-700 mt-1">Compliance sent this back: {task.decision_note}</p>
            )}
            {task.status === 'returned' && Number(task.compliance_suggestions) > 0 && (
              <p className="text-[12.5px] text-amber-800 mt-1">{task.compliance_suggestions_by || 'Compliance'} marked {task.compliance_suggestions} suggested change{Number(task.compliance_suggestions) === 1 ? '' : 's'} on the text. Open the policy to see each one in place.</p>
            )}
            {task.delegate_name && (
              <p className="text-[12px] text-slate-400 mt-1">Also with {task.delegate_name}</p>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0 flex-wrap">
          <button type="button" onClick={openRead}
            className="px-3.5 py-2 rounded-xl bg-slate-900 text-white text-[13px] font-medium hover:bg-slate-800">
            Read the policy
          </button>
          <button type="button" onClick={() => { setMode(mode === 'replace' ? null : 'replace'); setError(''); }}
            className="px-3.5 py-2 rounded-xl ring-1 ring-slate-200 text-[13px] font-medium text-slate-700 hover:ring-slate-300">
            Upload a revision
          </button>
          {teammates.length > 0 && (
            <button type="button" onClick={() => { setMode(mode === 'delegate' ? null : 'delegate'); setError(''); }}
              className="px-3.5 py-2 rounded-xl ring-1 ring-slate-200 text-[13px] text-slate-600 hover:ring-slate-300">
              Hand to a teammate
            </button>
          )}
        </div>
      </div>

      {mode === 'read' && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 overflow-y-auto p-3 sm:p-8"
          onClick={(e) => { if (e.target === e.currentTarget) closeWindow(); }}>
        <div className="mx-auto w-[min(1000px,100%)] rounded-2xl bg-slate-100 shadow-2xl p-4 sm:p-6 space-y-4">
          <div className="sticky top-0 z-10 -mx-4 sm:-mx-6 -mt-4 sm:-mt-6 px-4 sm:px-6 py-3 rounded-t-2xl bg-slate-100/95 backdrop-blur border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <p className="text-[15px] font-bold text-slate-900 truncate">{label}</p>
              <p className="text-[12px] text-slate-500">
                {task.status === 'submitted' ? 'Submitted to compliance.' : editCount > 0
                  ? `${editCount} change${editCount === 1 ? '' : 's'} in this review${draftSavedAt ? ` · saved ${new Date(draftSavedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : ' · not saved yet'}`
                  : draftSavedAt ? `Saved ${new Date(draftSavedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : 'Click any paragraph to change it. Save keeps your work; submit when you are done.'}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {task.status !== 'submitted' && (
                <>
                  <button type="button" disabled={draftBusy} onClick={() => saveDraft(false)}
                    className="px-3.5 py-2 rounded-xl bg-white ring-1 ring-slate-200 text-[13px] font-semibold text-slate-700 hover:ring-slate-300 disabled:opacity-50">
                    {draftBusy ? 'Saving…' : 'Save'}
                  </button>
                  <button type="button" onClick={openSubmit}
                    className="px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white text-[13px] font-semibold shadow-sm hover:from-orange-600 hover:to-orange-700">
                    Save and submit for approval
                  </button>
                </>
              )}
              <button type="button" onClick={closeWindow}
                className="px-3.5 py-2 rounded-xl bg-white ring-1 ring-slate-200 text-[13px] font-semibold text-slate-600 hover:ring-slate-300">
                Close
              </button>
            </div>
          </div>
          {finish && (
            <div className="fixed inset-0 z-[60] bg-slate-900/40 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) setFinish(false); }}>
              <div className="w-[min(560px,100%)] rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 p-6 space-y-4">
                <div>
                  <h3 className="text-[17px] font-bold text-slate-900">Submit for approval</h3>
                  <p className="text-[13px] text-slate-500 mt-1">
                    {editCount > 0
                      ? `Your ${editCount} change${editCount === 1 ? '' : 's'} and summary go to compliance for approval. Once approved they are written into the official document.`
                      : 'No changes were made. The policy is confirmed as current and sent to compliance.'}
                  </p>
                </div>
                {editCount > 0 && (
                  <textarea value={summary} onChange={e => setSummary(e.target.value)} rows={2}
                    placeholder="Summarize what changed — this goes into the document's revision table"
                    className="w-full rounded-xl ring-1 ring-slate-200 px-3.5 py-2.5 text-[13px] outline-none focus:ring-2 focus:ring-orange-300" />
                )}
                <p className="text-[13px] text-slate-700">
                  I have reviewed this policy and attest that it is accurate, current, and complete to the best of my knowledge and ability.
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <label className="block text-[12px] text-slate-500">Your full name
                    <input value={attestName} onChange={e => setAttestName(e.target.value)} placeholder="Type your full name" autoFocus
                      className="mt-1 w-full rounded-xl ring-1 ring-slate-200 px-3.5 py-2 text-[13px] text-slate-800 outline-none focus:ring-2 focus:ring-orange-300" />
                  </label>
                  <label className="block text-[12px] text-slate-500">Date of review
                    <input type="date" value={reviewedOn} onChange={e => setReviewedOn(e.target.value)}
                      className="mt-1 w-full rounded-xl ring-1 ring-slate-200 px-3 py-2 text-[13px] text-slate-800 outline-none focus:ring-2 focus:ring-orange-300" />
                  </label>
                </div>
                {finishNotice && <p className="text-[13px] text-sky-800">{finishNotice}</p>}
                {error && <p className="text-[13px] text-red-600">{error}</p>}
                <div className="flex items-center justify-end gap-2">
                  <button type="button" onClick={() => setFinish(false)} className="px-3.5 py-2 rounded-xl text-[13px] font-medium text-slate-600 hover:bg-slate-50">Back to the policy</button>
                  <button type="button" disabled={busy} onClick={finishReview}
                    className="px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white text-[13px] font-semibold disabled:opacity-50">
                    {busy ? 'Submitting…' : 'Submit for approval'}
                  </button>
                </div>
              </div>
            </div>
          )}
          {docParas == null ? null : docParas.format === 'docx' ? (
            <ReviewGuidance taskId={task.id} onData={setGuideData} />
          ) : (
            <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200 p-4">
              <p className="text-[13px] text-slate-600">
                This policy is a PDF, and the agent reads and assists on Word documents only. Review it from the document link below, then confirm it as current or upload a revised version. To get the agent's help on this policy, upload it as a Word document.
              </p>
            </div>
          )}
          {!policyDoc ? (
            <p className="text-[13px] text-slate-500">Loading the policy…</p>
          ) : policyDoc.missing ? (
            <p className="text-[13px] text-slate-500">The policy text is not available to read here. Ask your compliance team for the document.</p>
          ) : (
            <>
              {(policyDoc.files || []).length > 0 && (
                <p className="text-[12px] text-slate-500 mb-2">
                  Official document:{' '}
                  {policyDoc.files.slice(0, 2).map((f, i) => (
                    <a key={f.id} href={`${API}/api/ppm/library/${task.policy_id}/file/${f.id}`}
                      className="text-orange-700 hover:text-orange-800 font-medium">
                      {i > 0 ? ' · ' : ''}{f.file_name}
                    </a>
                  ))}
                </p>
              )}
              {docParas?.format === 'docx' ? (
                <>
                  <p className="text-[12px] text-slate-500">
                    Click any paragraph to edit it in place; hover for Add below and Erase. Your changes go to compliance and, once approved, are written into the official Word document with the revision date and revision table updated.
                  </p>
                  {placing && <p className="text-[12.5px] font-medium text-sky-800 mb-1">Placing the suggested wording into the policy…</p>}
                  <PolicyParagraphEditor paragraphs={docParas.paragraphs} blocks={docParas.blocks} docLook headerLogo={docParas.header_logo} value={paraOps} onChange={setParaOps} readOnly={task.status === 'submitted'}
                    proposals={proposals}
                    comments={(() => {
                      const skip = new Set(Object.keys(proposals).map(Number));
                      const fromGuide = task.status === 'submitted' ? {} : anchorFindings(guideData?.guidance?.sections, docParas.paragraphs, { skipIndexes: skip, skipHeadings: new Set(Object.values(proposals).map(p => String(p.heading || '').toLowerCase().trim())) });
                      const fromCompliance = anchorReturnedEdits(docParas.compliance_suggestions, docParas.compliance_suggestions_by || 'Compliance');
                      const merged = { ...fromGuide };
                      for (const [k, v] of Object.entries(fromCompliance)) merged[k] = [...(merged[k] || []), ...v];
                      return Object.keys(merged).length ? merged : null;
                    })()}
                    onResolveProposal={(idx, action, prop, beforeText) => {
                      if (action) {
                        fetch(`${API}/api/ppm-review/tasks/${task.id}/proposal-disposition`, {
                          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ index: idx, heading: prop?.heading || '', action, before: beforeText || '', after: prop?.after || '' }),
                        }).catch(() => {});
                      }
                      setProposals((prev) => { const next = { ...prev }; delete next[idx]; return next; });
                    }}
                    onDiscuss={task.status === 'submitted' ? null : (idx, sel) => setDiscuss(d => (d?.index === idx ? null : { index: idx, selected: sel }))}
                    discussIndex={discuss?.index}
                    renderDiscuss={() => (
                      <DiscussPanel task={task} index={discuss.index} selected={discuss.selected} onClose={() => setDiscuss(null)} />
                    )} />
                  {editCount > 0 && (
                    <div className="rounded-2xl bg-amber-50/60 ring-1 ring-amber-200 p-4 space-y-2.5">
                      <p className="text-[13px] text-slate-700 font-medium">
                        {editCount} change{editCount === 1 ? '' : 's'} ready to submit
                        {' '}({[editSummary.replaced && `${editSummary.replaced} edited`, editSummary.inserted && `${editSummary.inserted} added`, editSummary.deleted && `${editSummary.deleted} erased`].filter(Boolean).join(', ')}).
                      </p>
                      <textarea value={summary} onChange={e => setSummary(e.target.value)} rows={2}
                        placeholder="Summarize what changed — this goes into the document's revision table"
                        className="w-full rounded-xl ring-1 ring-slate-200 px-3.5 py-2.5 text-[13px] outline-none focus:ring-2 focus:ring-orange-300" />
                      <p className="text-[13px] text-slate-700">
                        I have reviewed this policy and attest that it is accurate, current, and complete to the best of my knowledge and ability.
                      </p>
                      <div className="flex gap-2 flex-wrap items-center">
                        <label className="flex items-center gap-1.5 text-[12px] text-slate-500">Date of review
                          <input type="date" value={reviewedOn} onChange={e => setReviewedOn(e.target.value)}
                            className="rounded-xl ring-1 ring-slate-200 px-2.5 py-2 text-[13px] text-slate-700 outline-none focus:ring-2 focus:ring-orange-300" />
                        </label>
                        <input value={attestName} onChange={e => setAttestName(e.target.value)} placeholder="Type your full name"
                          className="rounded-xl ring-1 ring-slate-200 px-3.5 py-2 text-[13px] outline-none focus:ring-2 focus:ring-orange-300" />
                        <button type="button" disabled={busy} onClick={submitEdits}
                          className="px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white text-[13px] font-semibold disabled:opacity-50">
                          {busy ? 'Submitting…' : 'Attest and submit changes'}
                        </button>
                      </div>
                      {error && <p className="text-[13px] text-red-600">{error}</p>}
                    </div>
                  )}
                </>
              ) : policyDoc.version?.content ? (
                <div className="max-h-80 overflow-y-auto rounded-xl ring-1 ring-slate-200 bg-slate-50/60 p-4">
                  <p className="text-[13px] text-slate-700 whitespace-pre-wrap leading-relaxed">{policyDoc.version.content}</p>
                </div>
              ) : (
                <p className="text-[13px] text-slate-500">No text version stored; use the document link above.</p>
              )}
            </>
          )}
        </div>
        </div>
      )}

      {mode === 'replace' && (
        <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
          <label className="block text-[13px] text-slate-600">
            Revised policy (Word .docx or PDF):
            <input type="file" accept=".docx,.pdf" onChange={e => setFile(e.target.files?.[0] || null)} className="block mt-1 text-[12px]" />
          </label>
          <textarea value={summary} onChange={e => setSummary(e.target.value)} rows={2}
            placeholder="What changed, in a sentence or two"
            className="w-full rounded-xl ring-1 ring-slate-200 px-3.5 py-2.5 text-[13px] outline-none focus:ring-2 focus:ring-orange-300" />
          <p className="text-[13px] text-slate-700">
            I have reviewed this policy and attest that it is accurate, current, and complete to the best of my knowledge and ability.
          </p>
          <div className="flex gap-2 flex-wrap items-center">
            <label className="flex items-center gap-1.5 text-[12px] text-slate-500">Date of review
              <input type="date" value={reviewedOn} onChange={e => setReviewedOn(e.target.value)}
                className="rounded-xl ring-1 ring-slate-200 px-2.5 py-2 text-[13px] text-slate-700 outline-none focus:ring-2 focus:ring-orange-300" />
            </label>
            <input value={attestName} onChange={e => setAttestName(e.target.value)} placeholder="Type your full name"
              className="rounded-xl ring-1 ring-slate-200 px-3.5 py-2 text-[13px] outline-none focus:ring-2 focus:ring-orange-300" />
            <button type="button" disabled={busy} onClick={submitReplace}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white text-[13px] font-semibold disabled:opacity-50">
              {busy ? 'Submitting…' : 'Attest and submit'}
            </button>
            <button type="button" onClick={() => setMode(null)} className="px-3 py-2 text-[13px] text-slate-500">Cancel</button>
          </div>
          {error && <p className="text-[13px] text-red-600">{error}</p>}
        </div>
      )}

      {mode === 'delegate' && (
        <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
          <label className="block text-[13px] text-slate-600">
            Who on your team should review this policy?
            <select value={delegateId} onChange={e => setDelegateId(e.target.value)}
              className="block mt-1 rounded-xl ring-1 ring-slate-200 px-3.5 py-2 text-[13px] bg-white outline-none focus:ring-2 focus:ring-orange-300">
              <option value="">Pick a teammate…</option>
              {teammates.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
            </select>
          </label>
          <p className="text-[12px] text-slate-500">They will get an email and see this policy on their home page. They attest in their own name and become the reviewer of record for it.</p>
          <div className="flex gap-2">
            <button type="button" disabled={busy} onClick={submitDelegate}
              className="px-4 py-2 rounded-xl bg-slate-900 text-white text-[13px] font-semibold disabled:opacity-50">
              {busy ? 'Sending…' : 'Hand it over'}
            </button>
            <button type="button" onClick={() => setMode(null)} className="px-3 py-2 text-[13px] text-slate-500">Cancel</button>
          </div>
          {error && <p className="text-[13px] text-red-600">{error}</p>}
        </div>
      )}
    </div>
  );
}

/**
 * Guidance from the Policy Review Guide, shown above the policy text while
 * the owner reads. Opening it kicks a background check (idempotent server
 * side; the same policy version is analyzed once and cached) and polls until
 * it lands. Guidance INFORMS ONLY — there is deliberately no button here that
 * changes the policy. The owner acts through Confirm or Upload a revision,
 * and compliance reviews everything.
 */
/**
 * The discuss panel, opened on the exact paragraph (with any selected words
 * carried along). Two things happen here, and neither decides for the user:
 * teach the agent (it checks the sources and may push back with the quote),
 * or ask compliance (the answer lands back here and by email). The wording
 * decision is always the owner's.
 */
export function DiscussPanel({ task, index, selected, onClose, viewer = 'owner' }) {
  const [mode, setMode] = useState('teach'); // 'teach' | 'ask'
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [result, setResult] = useState(null);   // teach reply
  const [decided, setDecided] = useState('');
  const [questions, setQuestions] = useState(null);

  useEffect(() => {
    if (mode !== 'ask' || questions !== null) return;
    fetch(`${API}/api/ppm-review/tasks/${task.id}/compliance-questions`, { credentials: 'include' })
      .then(r => r.json()).then(d => setQuestions(d.success ? (d.questions || []) : []))
      .catch(() => setQuestions([]));
  }, [mode, questions, task.id]);

  const submit = async () => {
    const t = text.trim();
    if (t.length < 8) { setNotice('Write it in a full sentence.'); return; }
    setBusy(true); setNotice(''); setResult(null);
    try {
      if (mode === 'teach') {
        const d = await fetch(`${API}/api/ppm-review/tasks/${task.id}/teach`, {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ paragraph_index: index, selected_text: selected || null, lesson: t }),
        }).then(r => r.json());
        if (d.success) { setResult(d); setText(''); }
        else setNotice(d.message || 'That did not go through. Try again.');
      } else {
        const d = await fetch(`${API}/api/ppm-review/tasks/${task.id}/ask-compliance`, {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ paragraph_index: index, selected_text: selected || null, question: t }),
        }).then(r => r.json());
        if (d.success) { setNotice('Sent. Compliance will answer here and by email — the wording decision stays yours.'); setText(''); setQuestions(null); }
        else setNotice(d.message || 'That did not go through. Try again.');
      }
    } catch { setNotice('That did not go through. Try again.'); }
    setBusy(false);
  };

  const decide = async (decision) => {
    if (!result?.id) return;
    try {
      await fetch(`${API}/api/ppm-review/lessons/${result.id}/decision`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision }),
      });
    } catch { /* the lesson stands either way */ }
    setDecided(decision === 'kept'
      ? 'Your call is recorded — the wording stays as written, and the agent remembers the lesson.'
      : 'Recorded. Click the paragraph to revise the wording; the agent remembers the lesson.');
  };
  const rerunReview = () => { window.dispatchEvent(new CustomEvent('ppm:rerun-review', { detail: { taskId: task.id } })); setNotice('The review is running again with this lesson. The result replaces the one above in a minute or two.'); };

  return (
    <div className="rounded-xl ring-1 ring-sky-200 bg-sky-50/50 p-3.5 my-1 space-y-2.5">
      <div className="flex items-center justify-between gap-2">
        <div className="inline-flex rounded-lg bg-white ring-1 ring-slate-200 p-0.5 gap-0.5">
          {(viewer === 'compliance' ? [['teach', 'Teach the agent']] : [['teach', 'Teach the agent'], ['ask', 'Ask compliance']]).map(([id, label]) => (
            <button key={id} type="button" onClick={() => { setMode(id); setNotice(''); }}
              className={`px-3 py-1 rounded-md text-[12px] font-semibold ${mode === id ? 'bg-sky-600 text-white' : 'text-slate-500 hover:text-slate-700'}`}>
              {label}
            </button>
          ))}
        </div>
        <button type="button" onClick={onClose} className="text-[12px] text-slate-500 hover:text-slate-700">Close</button>
      </div>
      {selected && <p className="text-[12px] text-slate-600 italic">About: &ldquo;{selected.slice(0, 240)}&rdquo;</p>}
      <div className="flex gap-2 items-start">
        <textarea value={text} onChange={e => setText(e.target.value)} rows={2} maxLength={mode === 'teach' ? 1200 : 2000}
          placeholder={mode === 'teach'
            ? 'e.g. This timeframe is intentional — our state contract sets 14 days, not 30.'
            : 'e.g. Does this wording still work for our D-SNP line of business?'}
          className="flex-1 rounded-xl ring-1 ring-slate-200 px-3.5 py-2 text-[13px] outline-none focus:ring-2 focus:ring-sky-300 bg-white" />
        <button type="button" disabled={busy} onClick={submit}
          className="px-4 py-2 rounded-xl bg-sky-600 hover:bg-sky-700 text-white text-[13px] font-semibold disabled:opacity-50">
          {busy ? (mode === 'teach' ? 'Checking the sources…' : 'Sending…') : (mode === 'teach' ? 'Teach' : 'Send to compliance')}
        </button>
      </div>
      <p className="text-[11.5px] text-slate-400">
        {mode === 'teach'
          ? 'The agent checks what you teach against the regulation, your state contract, and the memos on file — and pushes back with the exact quote if something conflicts. What to do about the wording is always your decision.'
          : 'Your question and this exact text go to the compliance team. Their answer is advice — the wording decision is yours.'}
      </p>
      {notice && <p className="text-[12.5px] font-medium text-sky-800">{notice}</p>}

      {result && (
        <div className={`rounded-lg p-3 ring-1 ${result.position === 'agreed' ? 'bg-emerald-50 ring-emerald-200' : result.position === 'rebutted' ? 'bg-amber-50 ring-amber-200' : 'bg-white ring-slate-200'}`}>
          <p className="text-[12px] font-semibold uppercase tracking-[0.05em] text-slate-600">
            {result.position === 'agreed' ? 'The agent will remember this' : result.position === 'rebutted' ? 'The agent pushes back' : 'The agent noted this'}
          </p>
          <p className="text-[13px] text-slate-700 mt-1 leading-relaxed">{result.note}</p>
          <button type="button" onClick={rerunReview} className="mt-2 px-3 py-1.5 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-[12px] font-semibold">Run the review again with this lesson</button>
          {(result.citations || []).map((c, i) => (
            <div key={i} className="mt-2 border-l-2 border-slate-300 pl-3">
              <p className="text-[12px] font-semibold text-slate-600">{c.source === 'state_contract' ? (c.label || 'State Medicaid contract') : c.source === 'memo' ? (c.label || 'HPMS memo') : c.citation}</p>
              <blockquote className="text-[12.5px] italic text-slate-600 mt-0.5">&ldquo;{c.quote}&rdquo;</blockquote>
            </div>
          ))}
          {result.position === 'rebutted' && !decided && (
            <div className="flex gap-3 mt-2.5">
              <button type="button" onClick={() => decide('kept')} className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-[12px] font-semibold">Keep my wording</button>
              <button type="button" onClick={() => decide('revised')} className="px-3 py-1.5 rounded-lg ring-1 ring-slate-300 text-[12px] font-semibold text-slate-700">I will revise it</button>
            </div>
          )}
          {decided && <p className="text-[12.5px] font-medium text-slate-600 mt-2">{decided}</p>}
        </div>
      )}

      {mode === 'ask' && (questions || []).map((qq) => (
        <div key={qq.id} className="rounded-lg bg-white ring-1 ring-slate-200 p-3">
          <p className="text-[13px] font-semibold text-slate-800">{qq.question}</p>
          {qq.selected_text && <p className="text-[11.5px] text-slate-400 italic mt-0.5">About: &ldquo;{qq.selected_text.slice(0, 160)}&rdquo;</p>}
          {qq.status === 'answered'
            ? <p className="text-[13px] text-slate-700 mt-1.5">{qq.answer}<span className="text-[11.5px] text-slate-400"> — {qq.answered_by_name || 'compliance'}</span></p>
            : <p className="text-[12px] text-amber-700 mt-1.5">Waiting on compliance.</p>}
        </div>
      ))}
    </div>
  );
}

function ReviewGuidance({ taskId, onData = null }) {

  const [guide, setGuide] = useState({ state: 'starting' }); // starting | generating | poll | ready | failed | error
  const [tick, setTick] = useState(0);

  // Kick the run once per open (the server claim makes duplicates harmless).
  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/ppm-review/tasks/${taskId}/guidance/generate`, { method: 'POST', credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (alive) setGuide(d?.status === 'ready' ? { state: 'poll' } : { state: 'generating' }); })
      .catch(() => { if (alive) setGuide({ state: 'error' }); });
    return () => { alive = false; };
  }, [taskId]);

  // Poll until a terminal state.
  useEffect(() => {
    if (!['starting', 'generating', 'poll'].includes(guide.state)) return undefined;
    let alive = true;
    fetch(`${API}/api/ppm-review/tasks/${taskId}/guidance`, { credentials: 'include' })
      .then(r => r.json().then(d => ({ status: r.status, d })))
      .then(({ status, d }) => {
        if (!alive) return;
        // 429: the limiter asked us to slow down; the run is still going, keep polling.
        if (status === 202 || status === 404 || status === 429) setGuide({ state: 'generating' });
        else if (d?.status === 'ready') { setGuide({ state: 'ready', data: d }); onData?.(d); }
        else if (d?.status === 'failed') setGuide({ state: 'failed', message: d.error_message });
        else setGuide({ state: 'error' });
      })
      .catch(() => { if (alive) setGuide({ state: 'error' }); });
    const t = setTimeout(() => { if (alive) setTick(n => n + 1); }, 8000);
    return () => { alive = false; clearTimeout(t); };
  }, [taskId, guide.state, tick]);

  const retry = () => {
    setGuide({ state: 'starting' });
    fetch(`${API}/api/ppm-review/tasks/${taskId}/guidance/generate`, { method: 'POST', credentials: 'include' })
      .then(() => setGuide({ state: 'generating' }))
      .catch(() => setGuide({ state: 'error' }));
  };
  // Run again: the same version is read again under the lessons taught since.
  const rerun = useCallback(() => {
    setGuide({ state: 'starting' });
    fetch(`${API}/api/ppm-review/tasks/${taskId}/guidance/generate`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ again: true }) })
      .then(() => setGuide({ state: 'generating' }))
      .catch(() => setGuide({ state: 'error' }));
  }, [taskId]);
  useEffect(() => {
    const onRerun = (e) => { if (e?.detail?.taskId === taskId) rerun(); };
    window.addEventListener('ppm:rerun-review', onRerun);
    return () => window.removeEventListener('ppm:rerun-review', onRerun);
  }, [taskId, rerun]);

  return (
    <div className="space-y-4">
      {guide.state === 'ready'
        ? (
          <>
            <GuidancePanel data={guide.data} />
            <div className="flex items-center justify-end -mt-2">
              <button type="button" onClick={rerun} title="Read this version again, applying every lesson taught since"
                className="text-[12px] font-semibold text-slate-600 hover:text-slate-900 underline-offset-2 hover:underline">Run the review again</button>
            </div>
          </>
        )
        : (
          <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200 p-4">
            {['starting', 'generating', 'poll'].includes(guide.state) && (
              <div className="flex items-center gap-2.5">
                <span className="inline-block h-3.5 w-3.5 rounded-full border-2 border-slate-300 border-t-slate-600 animate-spin" aria-hidden="true" />
                <p className="text-[13px] text-slate-600">
                  Checking this policy against current requirements — the regulatory corpus, recent final rules, CMS guidance, and your state contract. This can take a few minutes; you can keep reading meanwhile.
                </p>
              </div>
            )}
            {guide.state === 'failed' && (
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="text-[13px] text-slate-600">The check could not finish{guide.message ? `: ${guide.message}` : '.'}</p>
                <button type="button" onClick={retry} className="px-3 py-1.5 rounded-lg ring-1 ring-slate-200 text-[12px] text-slate-600 hover:ring-slate-300">Try again</button>
              </div>
            )}
            {guide.state === 'error' && (
              <p className="text-[13px] text-slate-600">Guidance is unavailable right now. Your review does not depend on it — the policy text is below.</p>
            )}
          </div>
        )}
      <AskGuidePanel taskId={taskId} />
    </div>
  );
}

/** Ready guidance: corpus vintage first, then the summary, flags, and clean sections. */
function GuidancePanel({ data }) {
  // Stored answers to conditional questions, keyed by the question text.
  const [condAnswers, setCondAnswers] = useState(null);
  useEffect(() => { fetchConditionAnswers().then(setCondAnswers); }, []);
  const g = data?.guidance || {};
  const sections = Array.isArray(g.sections) ? g.sections : [];
  const flags = sections.filter(s => s.status === 'flag');
  const unverified = sections.filter(s => s.status === 'unverified');
  const unassessed = sections.filter(s => s.status === 'no_assessment');
  const clean = sections.filter(s => s.status === 'no_changes');
  const corpusNotice = data?.corpus_meta?.notice;
  const when = data?.generated_at
    ? new Date(data.generated_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric' }) : null;

  return (
    <div className="rounded-xl ring-1 ring-slate-200 bg-slate-50/70 p-4 space-y-3">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <p className="text-[12px] font-semibold uppercase tracking-[0.05em] text-slate-500">Checked against current requirements</p>
        <p className="text-[11px] text-slate-400">
          {flags.length ? `${flags.length} ${flags.length === 1 ? 'section' : 'sections'} flagged` : 'No sections flagged'}
          {when ? ` · ${when}` : ''}{data?.citations_verified ? ` · ${data.citations_verified} verified citations` : ''}
        </p>
      </div>

      {corpusNotice && (
        <div className="rounded-lg bg-amber-50 ring-1 ring-amber-200 px-3 py-2.5">
          <p className="text-[12.5px] text-amber-800">{corpusNotice}</p>
        </div>
      )}

      {g.overall_summary && <p className="text-[13px] text-slate-700 leading-relaxed">{g.overall_summary}</p>}

      {flags.filter(s => !(s.applies === 'conditional' && s.condition)).length > 0 && (
        <p className="text-[12.5px] font-medium text-sky-800 bg-sky-50 ring-1 ring-sky-200 rounded-lg px-3 py-2">
          The suggested wording is placed on the policy text below, written in the policy's own words — use each change, adjust it, dismiss it, or ask.
        </p>
      )}
      {flags.filter(s => s.applies === 'conditional' && s.condition).map(s => (
        <div key={s.index} className="rounded-lg bg-white ring-1 ring-slate-200 p-3.5">
          <p className="text-[13px] font-semibold text-slate-800">{s.heading}</p>
          <ConditionAnswer condition={s.condition} stored={condAnswers?.get(s.condition)} />
          <p className="text-[13px] text-slate-700 mt-1 leading-relaxed">{s.note}</p>
          {(s.citations || []).map((c, i) => (
            <div key={i} className="mt-2.5 border-l-2 border-slate-300 pl-3">
              <p className="text-[12px] font-semibold text-slate-600">
                {c.source === 'state_contract' ? (c.label || 'State Medicaid contract') : c.citation}
              </p>
              <blockquote className="text-[12.5px] italic text-slate-600 mt-0.5 leading-relaxed">&ldquo;{c.quote}&rdquo;</blockquote>
            </div>
          ))}
        </div>
      ))}

      <TakeOnBlock items={g.take_on} />

      {unverified.length > 0 && (
        <p className="text-[11.5px] text-slate-400">
          {unverified.length} observation{unverified.length === 1 ? '' : 's'} could not be verified word-for-word against the final rule, your state contract, or the memos on file — nothing is shown for {unverified.length === 1 ? 'it' : 'them'}.
        </p>
      )}

      {unassessed.length > 0 && (
        <p className="text-[12.5px] text-amber-700">
          Not assessed in this run (treat as unreviewed, not cleared): {unassessed.map(s => s.heading).join('; ')}.
        </p>
      )}

      {clean.length > 0 && (
        <p className="text-[12.5px] text-slate-500">
          No changes found affecting: {clean.map(s => s.heading).join('; ')}.
        </p>
      )}

      {(g.corpus_gaps || []).length > 0 && (
        <div className="pt-1">
          <p className="text-[12px] font-semibold text-slate-500">What this check could not cover</p>
          {g.corpus_gaps.map((gap, i) => <p key={i} className="text-[12px] text-slate-500 mt-0.5">{gap}</p>)}
        </div>
      )}

      <p className="text-[11.5px] text-slate-400 pt-1 border-t border-slate-200">
        This guidance informs your review; it never changes the policy. If it holds up, confirm the policy; if changes are needed, upload a revision. Compliance reviews everything.
      </p>
    </div>
  );
}

/** Ask the guide a question about this policy. Grounded answers, verified quotes, rate-limited. */
export function AskGuidePanel({ taskId, endpoint = 'ask', title = 'Ask about this policy' }) {
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [items, setItems] = useState([]);   // prior + new Q&A, newest first
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    if (!open) return;
    fetch(`${API}/api/ppm-review/tasks/${taskId}/questions`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d?.success) setItems((d.questions || []).map(q => ({ question: q.question, ...(q.answer || {}), created_at: q.created_at }))); })
      .catch(() => {});
  }, [open, taskId]);

  const ask = async () => {
    const q = question.trim();
    if (q.length < 5) { setNotice('Ask a fuller question.'); return; }
    setBusy(true); setNotice('');
    try {
      const r = await fetch(`${API}/api/ppm-review/tasks/${taskId}/${endpoint}`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: q }),
      });
      const d = await r.json();
      if (d.success) { setItems(prev => [d, ...prev]); setQuestion(''); }
      else setNotice(d.message || 'That did not go through. Try again.');
    } catch { setNotice('That did not go through. Try again.'); }
    setBusy(false);
  };

  return (
    <div className="rounded-xl ring-1 ring-slate-200 bg-white p-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-[12px] font-semibold uppercase tracking-[0.05em] text-slate-500">{title}</p>
        <button type="button" onClick={() => setOpen(v => !v)} className="text-[12px] text-slate-500 hover:text-slate-700">
          {open ? 'Hide' : 'Open'}
        </button>
      </div>
      {open && (
        <div className="mt-3 space-y-3">
          <div className="flex gap-2 items-start">
            <input value={question} onChange={e => setQuestion(e.target.value)} maxLength={600}
              onKeyDown={e => { if (e.key === 'Enter' && !busy) ask(); }}
              placeholder="e.g. Does this policy still match the current appeals timeframes?"
              className="flex-1 rounded-xl ring-1 ring-slate-200 px-3.5 py-2 text-[13px] outline-none focus:ring-2 focus:ring-orange-300" />
            <button type="button" disabled={busy} onClick={ask}
              className="px-4 py-2 rounded-xl bg-slate-900 text-white text-[13px] font-semibold disabled:opacity-50">
              {busy ? 'Checking the sources…' : 'Ask'}
            </button>
          </div>
          <p className="text-[11.5px] text-slate-400">
            Answers quote the regulation or your state contract word for word, and are verified before you see them. Whether to confirm or revise the policy stays your decision, reviewed by compliance.
          </p>
          {notice && <p className="text-[12.5px] text-amber-700">{notice}</p>}
          {items.map((it, i) => (
            <div key={i} className="rounded-lg bg-slate-50 ring-1 ring-slate-200 p-3.5">
              <p className="text-[13px] font-semibold text-slate-800">{it.question}</p>
              <p className="text-[13px] text-slate-700 mt-1.5 leading-relaxed">{it.answer}</p>
              {(it.citations || []).map((c, j) => (
                <div key={j} className="mt-2 border-l-2 border-slate-300 pl-3">
                  <p className="text-[12px] font-semibold text-slate-600">
                    {c.source === 'state_contract' ? (c.label || 'State Medicaid contract') : c.citation}
                  </p>
                  <blockquote className="text-[12.5px] italic text-slate-600 mt-0.5">&ldquo;{c.quote}&rdquo;</blockquote>
                </div>
              ))}
              {(it.caveats || []).map((cv, j) => <p key={j} className="text-[11.5px] text-slate-400 mt-1.5">{cv}</p>)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The item's working thread: comments between the owner and compliance, the
 * closure request, and every lifecycle event, in one modal. The server only
 * serves this to the assigned owner (or their department) and to compliance,
 * so what a user can open here is exactly what is theirs.
 */
function SmacItemModal({ item, onClose, onChanged }) {
  const [events, setEvents] = useState(null);
  const [closurePending, setClosurePending] = useState(false);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    const d = await fetch(`${API}/api/smac/obligations/${item.id}/activity`, { credentials: 'include' })
      .then(r => r.json()).catch(() => null);
    if (d?.success) { setEvents(d.events); setClosurePending(d.closure_pending); }
    else setError(d?.message || 'The history could not be loaded.');
  }, [item.id]);
  useEffect(() => { load(); }, [load]);

  const addComment = async () => {
    const text = comment.trim();
    if (!text) return;
    setBusy(true); setError('');
    const d = await fetch(`${API}/api/smac/obligations/${item.id}/comments`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: text }),
    }).then(r => r.json()).catch(() => null);
    if (d?.success) { setComment(''); await load(); }
    else setError(d?.message || 'The comment could not be saved.');
    setBusy(false);
  };

  const requestClosure = async () => {
    setBusy(true); setError('');
    const d = await fetch(`${API}/api/smac/obligations/${item.id}/request-closure`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: comment.trim() || null }),
    }).then(r => r.json()).catch(() => null);
    if (d?.success) { setComment(''); await load(); onChanged?.(); }
    else setError(d?.message || 'The request could not be sent.');
    setBusy(false);
  };

  const LABEL = {
    comment: 'commented', flagged: 'raised a problem', completed: 'completed the item',
    attested: 'confirmed it is in place', activated: 'activated the item', retired: 'retired the item',
    assigned: 'assigned the item', closure_requested: 'asked for this to be closed',
    closure_declined: 'declined the closure request',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-label={item.title}>
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col">
        <div className="px-5 pt-5 pb-4 border-b border-slate-100">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[15px] font-semibold text-slate-900">{item.title}</p>
              <p className="text-[12px] text-slate-400 mt-0.5">From your plan's {item.state_code} state contract</p>
            </div>
            <button type="button" onClick={onClose} title="Close"
              className="px-2.5 py-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 text-sm">✕</button>
          </div>
          {closurePending && (
            <p className="mt-3 text-[12.5px] px-3 py-2 rounded-lg bg-amber-50 text-amber-800 border border-amber-200">
              Closure has been requested. Compliance is reviewing it.
            </p>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {/* Deliverables carry the plan advisor; standing duties have no plan
              to advise on, so the panel simply is not there for them. */}
          {item.category === 'dated' && <AdvicePanel obligationId={item.id} />}
          {events === null && !error && <p className="text-[13px] text-slate-400">Loading…</p>}
          {events !== null && events.length === 0 && (
            <p className="text-[13px] text-slate-400">Nothing here yet. Leave the first note.</p>
          )}
          {(events || []).map(e => (
            <div key={e.id} className={`rounded-xl px-3.5 py-2.5 ${e.action === 'comment' ? 'bg-slate-50 ring-1 ring-slate-100' : ''}`}>
              <p className="text-[12px] text-slate-400">
                <span className="font-medium text-slate-600">{e.user_name || 'Someone'}</span>
                {' '}{LABEL[e.action] || e.action}
                {' · '}{new Date(e.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </p>
              {e.note && <p className="text-[13px] text-slate-700 mt-1 whitespace-pre-wrap">{e.note}</p>}
            </div>
          ))}
        </div>

        <div className="px-5 py-4 border-t border-slate-100 space-y-2.5">
          <textarea value={comment} onChange={e => setComment(e.target.value)} rows={2}
            placeholder="Write a comment for compliance…"
            className="w-full rounded-xl ring-1 ring-slate-200 px-3.5 py-2.5 text-[13px] outline-none focus:ring-2 focus:ring-orange-300" />
          {error && <p className="text-[13px] text-red-600">{error}</p>}
          <div className="flex items-center gap-2">
            <button type="button" disabled={busy || !comment.trim()} onClick={addComment}
              title="Add your comment to this item's thread"
              className="px-4 py-2 rounded-xl bg-slate-900 text-white text-[13px] font-semibold disabled:opacity-40">
              Comment
            </button>
            {!closurePending && (
              <button type="button" disabled={busy} onClick={requestClosure}
                title="Ask compliance to close this item. It stays open until they approve."
                className="px-4 py-2 rounded-xl ring-1 ring-slate-200 text-[13px] text-slate-600 hover:ring-slate-300 disabled:opacity-40">
                Request closure
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SmacItemCard({ item, onDone }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [showThread, setShowThread] = useState(false);

  const due = item.next_due_date ? String(item.next_due_date).slice(0, 10) : null;
  const today = new Date().toISOString().slice(0, 10);
  const overdue = due && due < today;
  const dueLabel = due
    ? new Date(due + 'T12:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : null;
  const attest = item.category === 'structural';

  const submit = async () => {
    setBusy(true); setError('');
    try {
      const fd = new FormData();
      if (note) fd.append('note', note);
      if (file) fd.append('evidence', file);
      const d = await fetch(`${API}/api/smac/obligations/${item.id}/complete`, {
        method: 'POST', credentials: 'include', body: fd,
      }).then(r => r.json());
      if (d.success) { setOpen(false); onDone(); }
      else setError(d.message || 'That did not go through. Try again.');
    } catch { setError('That did not go through. Try again.'); }
    setBusy(false);
  };

  const flag = async () => {
    const text = window.prompt('Tell compliance what is wrong or blocking you:');
    if (!text) return;
    await fetch(`${API}/api/smac/obligations/${item.id}/flag`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: text }),
    }).catch(() => {});
    window.alert('Sent. Compliance will see your note on this item.');
  };

  return (
    <div className={`rounded-2xl bg-white ring-1 p-5 ${overdue ? 'ring-red-200' : 'ring-slate-200'}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[15px] font-semibold text-slate-900">{item.title}</p>
          <p className="text-[13px] text-slate-600 mt-1">{item.obligation_text}</p>
          <p className="text-[12px] text-slate-400 mt-1.5">
            From your plan's {item.state_code} state contract{dueLabel ? ` · ${overdue ? 'was due' : 'due'} ${dueLabel}` : ''}
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button type="button" onClick={() => setOpen(v => !v)}
            className="px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white text-[13px] font-semibold hover:opacity-90 transition">
            {attest ? 'Confirm it’s in place' : 'Mark complete'}
          </button>
          <button type="button" onClick={() => setShowThread(true)}
            title="Comments, history, and requesting closure"
            className="px-3 py-2 rounded-xl ring-1 ring-slate-200 text-[13px] text-slate-600 hover:ring-slate-300 transition">
            Details
          </button>
          <button type="button" onClick={flag}
            className="px-3 py-2 rounded-xl ring-1 ring-slate-200 text-[13px] text-slate-600 hover:ring-slate-300 transition">
            Something’s wrong
          </button>
        </div>
      </div>

      {showThread && (
        <SmacItemModal item={item} onClose={() => setShowThread(false)} onChanged={onDone} />
      )}

      {open && (
        <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
          <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
            placeholder={attest ? 'Anything worth noting (optional)' : 'What was done, in a sentence (optional)'}
            className="w-full rounded-xl ring-1 ring-slate-200 px-3.5 py-2.5 text-[13px] outline-none focus:ring-2 focus:ring-orange-300" />
          {!attest && (
            <label className="block text-[13px] text-slate-600">
              Attach the deliverable if there is one:
              <input type="file" onChange={e => setFile(e.target.files?.[0] || null)} className="block mt-1 text-[12px]" />
            </label>
          )}
          {error && <p className="text-[13px] text-red-600">{error}</p>}
          <div className="flex gap-2">
            <button type="button" disabled={busy} onClick={submit}
              className="px-4 py-2 rounded-xl bg-slate-900 text-white text-[13px] font-semibold disabled:opacity-50">
              {busy ? 'Saving…' : (attest ? 'Confirm' : 'Done')}
            </button>
            <button type="button" onClick={() => setOpen(false)} className="px-3 py-2 text-[13px] text-slate-500">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function formatDueDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const target      = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const days = Math.round((target.getTime() - startOfToday.getTime()) / (1000 * 60 * 60 * 24));
  if (days < 0)   return `Overdue ${-days} day${-days === 1 ? '' : 's'}`;
  if (days === 0) return 'Due today';
  if (days === 1) return 'Due tomorrow';
  if (days < 7)   return `Due in ${days} days`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function GreetingLine({ greeting, firstName }) {
  const verbs = {
    morning:   'Good morning',
    afternoon: 'Good afternoon',
    evening:   'Good evening',
  };
  const verb = verbs[greeting] || 'Welcome';
  return (
    <h1 className="text-[34px] font-semibold text-slate-900 tracking-tight leading-tight">
      {verb}{firstName ? `, ${firstName}` : ''}.
    </h1>
  );
}

function SkeletonStrip() {
  return (
    <div className="flex gap-3 overflow-x-auto pb-2">
      {[0, 1, 2, 3].map(i => (
        <div key={i} className="shrink-0 w-[260px] h-[110px] rounded-2xl bg-slate-100 animate-pulse" />
      ))}
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {[0, 1, 2, 3, 4, 5].map(i => (
        <div key={i} className="h-[148px] rounded-2xl bg-slate-100 animate-pulse" />
      ))}
    </div>
  );
}

function TodayStrip({ items, loading }) {
  if (loading) return <SkeletonStrip />;

  if (!items || items.length === 0) {
    return (
      <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-8 flex items-center gap-4">
        <div className="w-10 h-10 rounded-full bg-emerald-50 ring-1 ring-emerald-100 flex items-center justify-center text-emerald-600">
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <div>
          <p className="text-[15px] font-medium text-slate-900">You're all caught up.</p>
          <p className="text-[13px] text-slate-500 mt-0.5">Nothing due in the next seven days.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory
        [&::-webkit-scrollbar]:h-1.5
        [&::-webkit-scrollbar-thumb]:bg-slate-200
        [&::-webkit-scrollbar-thumb]:rounded-full">
      {items.map((item, i) => {
        const due = formatDueDate(item.due_date);
        const overdue = due && due.startsWith('Overdue');
        return (
          <a
            key={`${item.app}-${i}`}
            href={item.href}
            className="shrink-0 w-[260px] snap-start rounded-2xl bg-white ring-1 ring-slate-200 p-4 hover:ring-slate-300 hover:-translate-y-0.5 transition-all duration-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
          >
            <div className="flex items-center gap-2 mb-3">
              <span className="text-base leading-none">{APP_ICON[item.app] || '•'}</span>
              <span className="text-[11px] uppercase tracking-wide font-medium text-slate-400">{item.app}</span>
            </div>
            <p className="text-[14px] font-medium text-slate-900 line-clamp-2 leading-snug min-h-[2.6em]">
              {item.title || 'Untitled'}
            </p>
            {due && (
              <p className={`text-[12px] mt-3 font-medium ${overdue ? 'text-red-600' : 'text-slate-500'}`}>
                {due}
              </p>
            )}
          </a>
        );
      })}
    </div>
  );
}

function AppCard({ app, onClick }) {
  // External apps (memo, knowledge, scrubber) render as anchors so the
  // browser handles top-level navigation natively. In-Core apps go through
  // the parent's onSelectApp to flip the routing state without a reload.
  const inner = (
    <>
      <div className="flex items-start gap-3">
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
          style={{ backgroundColor: (app.color || '#64748b') + '1A' }}
        >
          {APP_ICON[app.key] || '•'}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-[15px] font-semibold text-slate-900">{app.label}</h3>
            {Number.isInteger(app.open_count) && app.open_count > 0 && (
              <span className="shrink-0 inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full text-[11px] font-semibold bg-slate-900 text-white tabular-nums">
                {app.open_count}
              </span>
            )}
          </div>
          <p className="text-[13px] text-slate-500 mt-1 leading-snug">{app.desc}</p>
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between">
        <span className="text-[12px] text-slate-400">
          {Number.isInteger(app.open_count) && app.open_count > 0
            ? `${app.open_count} open ${app.open_count === 1 ? 'item' : 'items'}`
            : Number.isInteger(app.open_count) && app.open_count === 0
              ? 'Nothing open'
              : 'Open the app'}
        </span>
        <svg className="w-4 h-4 text-slate-300 group-hover:text-slate-500 group-hover:translate-x-0.5 transition-all" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 12h14M13 5l7 7-7 7" />
        </svg>
      </div>
    </>
  );
  const className = "group block text-left rounded-2xl bg-white ring-1 ring-slate-200 p-5 hover:ring-slate-300 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(15,23,42,0.06)] transition-all duration-200 w-full";
  if (app.external) {
    return <a href={app.href} className={className}>{inner}</a>;
  }
  return (
    <button type="button" onClick={() => onClick(app.key)} className={className}>
      {inner}
    </button>
  );
}

export default function Workspace({ user, onSelectApp }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Launchpad opens on arrival — this IS the app-picking experience here,
  // exactly like PSI's top-right launcher. Reopen: button or Ctrl/Cmd+K.
  // The launcher opens on arrival — except when the person came in through
  // the annual-review email button: they were promised their policies, not
  // a modal over them. (PolicyReviewSection consumes the stored id after
  // this initializer runs.)
  const [launcherOpen, setLauncherOpen] = useState(() => {
    try { return !sessionStorage.getItem('pcg_open_review'); } catch { return true; }
  });

  useEffect(() => {
    const onHotkey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) { e.preventDefault(); setLauncherOpen(s => !s); }
    };
    window.addEventListener('keydown', onHotkey);
    return () => window.removeEventListener('keydown', onHotkey);
  }, []);

  // The launcher is theme-aware (PSI passes its dark/light helper); the
  // Workspace is a light page, so always resolve to the light variant.
  const lightT = (light) => light;

  const tz = useMemo(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; }
    catch { return 'UTC'; }
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/workspace/summary`, {
      credentials: 'include',
      headers: { 'X-Tz': tz },
    })
      .then(r => r.json())
      .then(d => {
        if (cancelled) return;
        if (d?.success) setSummary(d);
        else setError(d?.message || 'Failed to load');
      })
      .catch(err => !cancelled && setError(err?.message || 'Failed to load'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [tz]);

  // State-contract items assigned to this user (SMAC register). Quietly
  // absent when there are none — most users never see this section.
  const [smacItems, setSmacItems] = useState([]);
  const reloadSmac = useCallback(() => {
    fetch(`${API}/api/smac/my`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d?.success) setSmacItems(d.obligations || []); })
      .catch(() => {});
  }, []);
  useEffect(() => { reloadSmac(); }, [reloadSmac]);

  const firstName = summary?.user?.first_name || user?.name?.split(' ')?.[0] || '';
  const greeting  = summary?.greeting || 'morning';
  const apps      = summary?.apps || [];
  const today     = summary?.today || [];
  const isComp    = summary?.user?.is_compliance;

  // Subtitle line: tells the user where they stand without overpromising.
  const subtitle = isComp
    ? 'Workspace view — your scoped open items.'
    : apps.length === 0
      ? "You don't have any apps assigned yet. Reach out to your compliance team and they'll set you up."
      : "Here's what's on your plate today.";

  return (
    <div className="min-h-full bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-5xl mx-auto px-6 sm:px-8 pt-14 pb-20">

        {/* ── Greeting + top-right launcher button ───────────────────── */}
        <header className="mb-10 flex items-start justify-between gap-4">
          <div>
            <GreetingLine greeting={greeting} firstName={firstName} />
            <p className="mt-2 text-[14px] text-slate-500">{subtitle}</p>
          </div>
          <button
            type="button"
            onClick={() => setLauncherOpen(true)}
            title="Apps (Ctrl+K)"
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white ring-1 ring-slate-200 shadow-sm hover:ring-slate-300 hover:shadow transition text-[13px] font-medium text-slate-700 flex-shrink-0"
          >
            <svg className="w-4 h-4 text-slate-500" fill="currentColor" viewBox="0 0 16 16">
              <rect x="1" y="1" width="6" height="6" rx="1.5"/><rect x="9" y="1" width="6" height="6" rx="1.5"/>
              <rect x="1" y="9" width="6" height="6" rx="1.5"/><rect x="9" y="9" width="6" height="6" rx="1.5"/>
            </svg>
            Apps
            <kbd className="hidden sm:inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-md text-slate-400 bg-slate-100">⌘K</kbd>
          </button>
        </header>

        {/* ── The Compliance Agent's cards — absent when the agent is off or silent ── */}
        <AgentStrip />

        {/* ── Annual policy review — only when this user has open tasks ── */}
        <PolicyReviewSection />

        {/* ── State-contract items (SMAC) — only when this user owns any ── */}
        {smacItems.length > 0 && (
          <section className="mb-12">
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="text-[13px] font-semibold uppercase tracking-[0.06em] text-slate-500">Your state contract items</h2>
              <span className="text-[12px] text-slate-400">{smacItems.length} {smacItems.length === 1 ? 'item' : 'items'}</span>
            </div>
            <div className="space-y-3">
              {smacItems.map(it => (
                <SmacItemCard key={it.id} item={it} onDone={reloadSmac} />
              ))}
            </div>
          </section>
        )}

        {/* ── Today strip ────────────────────────────────────────────── */}
        <section className="mb-12">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-[13px] font-semibold uppercase tracking-[0.06em] text-slate-500">Today</h2>
            {!loading && today.length > 0 && (
              <span className="text-[12px] text-slate-400">{today.length} {today.length === 1 ? 'item' : 'items'}</span>
            )}
          </div>
          {error
            ? <div className="rounded-2xl bg-white ring-1 ring-amber-200 p-4 text-[13px] text-amber-800">{error}</div>
            : <TodayStrip items={today} loading={loading} />}
        </section>

        {/* ── Apps — the shared PSI Launchpad, no in-page grid ────────── */}
        <section>
          <button
            type="button"
            onClick={() => setLauncherOpen(true)}
            className="w-full rounded-2xl bg-white ring-1 ring-slate-200 p-8 text-center hover:ring-slate-300 hover:shadow-sm transition"
          >
            <p className="text-[15px] font-medium text-slate-900">Open your apps</p>
            <p className="text-[13px] text-slate-500 mt-1">Click here, use the Apps button above, or press Ctrl+K anytime.</p>
          </button>
        </section>

      </div>

      {/* The exact PSI Launchpad — opens on arrival, filtered to this user.
          When work is waiting, one line on the launcher says so; clicking it
          reveals this page, where the cards are. */}
      <AppLauncher
        open={launcherOpen}
        onClose={() => setLauncherOpen(false)}
        onSelectApp={onSelectApp}
        user={user}
        t={lightT}
        hint={(() => {
          const week = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
          const n = smacItems.filter(i => i.next_due_date && String(i.next_due_date).slice(0, 10) <= week).length + (summary?.today || []).length;
          return n > 0 ? `${n} ${n === 1 ? 'thing needs' : 'things need'} you this week — tap to see` : null;
        })()}
      />
    </div>
  );
}
